import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Phone,
  ShieldCheck,
} from 'lucide-react';
import { apiRequest } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { TENANT } from '../config';
import { Button } from '../components/new-components/ui/button';
import { MonthCalendar, dateKey } from '../components/new-components/MonthCalendar';
import { formatPhone } from '../utils/formatPhone';

type MeetingType = {
  id: string;
  key: string;
  label: string;
  purpose?: string;
  default_cta?: string;
  duration_minutes: number;
  location_type: string;
  host_name?: string;
  host_email?: string;
};

type Slot = {
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  meeting_type: string;
  label: string;
};

type OnboardingConfig = {
  brand_name: string;
  meeting_type: MeetingType;
  user: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    document_access_status?: string;
    manual_review_status?: string;
    investment_eligibility_status?: string;
  };
  upcoming_event?: {
    id: string;
    title: string;
    starts_at: string;
    ends_at: string;
    timezone: string;
    status: string;
    host_name?: string;
    host_email?: string;
  } | null;
};

type BookingResult = {
  booking: Record<string, unknown>;
  add_to_calendar_url: string;
  formatted_time: string;
};

const emptyForm = {
  first_name: '',
  last_name: '',
  phone: '',
  notes: ''
};

// Copy only — all color comes from the design tokens
const theme = TENANT.id === 'redex'
  ? {
      title: 'Schedule your onboarding call',
      body: 'Meet with Investor Relations to confirm your profile, review platform access, and clarify next steps before offering materials are released.'
    }
  : {
      title: 'Schedule your onboarding call',
      body: 'Meet with Investor Relations to confirm your profile, review document access, and walk through the next step in your investor onboarding path.'
    };

function formatDay(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  }).format(new Date(value));
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
}

interface OnboardingCallBookingProps {
  /** When provided, the flow renders as a modal body (no page chrome / card) and this closes it. */
  onClose?: () => void;
  /** Booking source tag; falls back to the ?source= query param. */
  source?: string;
  /** Fired once a booking is confirmed — lets the portal reflect the scheduled call. */
  onBooked?: () => void;
}

export default function OnboardingCallBooking({ onClose, source: sourceProp, onBooked }: OnboardingCallBookingProps = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const isModal = typeof onClose === 'function';
  const source = sourceProp || searchParams.get('source') || 'investor-progress';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

  const [config, setConfig] = useState<OnboardingConfig | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [calMonth, setCalMonth] = useState<Date | null>(null);
  const [step, setStep] = useState<'time' | 'details'>('time');
  // True once the user moves between the two steps, so the initial calendar doesn't slide in on top
  // of the modal's own open animation — only real step transitions get the directional swap.
  const [hasNavigated, setHasNavigated] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [confirmation, setConfirmation] = useState<BookingResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');

  // `isActive` lets each load discard a stale/unmounted response: this flow is mounted and
  // unmounted by SchedulerModal, and loadConfig re-fires when `user` changes (refreshUser),
  // so an earlier request can resolve after unmount or out of order.
  const loadConfig = useCallback(async (isActive: () => boolean = () => true) => {
    setLoading(true);
    setError('');
    const response = await apiRequest<OnboardingConfig>('GET', '/onboarding-call/config');
    if (!isActive()) return;

    const nextConfig = (response.success && response.data) ? response.data : null;

    if (!nextConfig) {
      setError(response.message || response.error || 'Onboarding call details could not be loaded.');
      setLoading(false);
      return;
    }

    setConfig(nextConfig);
    setForm(prev => ({
      first_name: prev.first_name || nextConfig.user.first_name || user?.first_name || '',
      last_name: prev.last_name || nextConfig.user.last_name || user?.last_name || '',
      phone: formatPhone(prev.phone || nextConfig.user.phone || user?.phone || ''),
      notes: prev.notes
    }));
    setLoading(false);
  }, [user?.id, user?.email, user?.first_name, user?.last_name, user?.phone]);

  const loadSlots = useCallback(async (isActive: () => boolean = () => true) => {
    setLoadingSlots(true);
    setError('');
    const response = await apiRequest<{ slots: Slot[]; meeting_type: MeetingType }>('GET', '/onboarding-call/slots', undefined, {
      params: { timezone }
    });
    if (!isActive()) return;
    if (!response.success || !response.data) {
      setError(response.message || response.error || 'Available times could not be loaded.');
      setSlots([]);
    } else {
      setSlots(response.data.slots || []);
    }
    setLoadingSlots(false);
  }, [timezone]);

  useEffect(() => {
    let active = true;
    loadConfig(() => active);
    return () => { active = false; };
  }, [loadConfig]);

  useEffect(() => {
    let active = true;
    loadSlots(() => active);
    return () => { active = false; };
  }, [loadSlots]);

  // Modal: when the step changes (e.g. picking a time → details), jump back to the top so the
  // new step doesn't inherit the previous step's scroll position.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isModal) rootRef.current?.scrollIntoView({ block: 'start' });
  }, [step, isModal]);

  // Group slots by local calendar date so the calendar can map a clicked day → its times.
  const slotsByDate = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const slot of slots) {
      const key = dateKey(new Date(slot.starts_at));
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(slot);
    }
    return map;
  }, [slots]);

  const availableKeys = useMemo(() => new Set(slotsByDate.keys()), [slotsByDate]);
  const sortedKeys = useMemo(() => Array.from(slotsByDate.keys()).sort(), [slotsByDate]);

  const firstOfMonthFromKey = (key: string) => {
    const [y, mo] = key.split('-').map(Number);
    return new Date(y, mo - 1, 1);
  };
  const monthBounds = useMemo(
    () => (sortedKeys.length ? { min: firstOfMonthFromKey(sortedKeys[0]), max: firstOfMonthFromKey(sortedKeys[sortedKeys.length - 1]) } : null),
    [sortedKeys]
  );

  // Default to the first available date (and its month) so times are visible immediately.
  useEffect(() => {
    if (!selectedDateKey && sortedKeys.length) {
      setSelectedDateKey(sortedKeys[0]);
      setCalMonth(firstOfMonthFromKey(sortedKeys[0]));
    }
  }, [sortedKeys, selectedDateKey]);

  const selectedDaySlots = selectedDateKey ? (slotsByDate.get(selectedDateKey) ?? []) : [];

  const canBook = selectedSlot && form.first_name.trim() && form.last_name.trim() && !booking;
  const email = config?.user.email || user?.email || '';
  const hostName = config?.meeting_type.host_name || 'Investor Relations';

  const updateField = (key: keyof typeof emptyForm, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Pick a time → advance straight to the details step (chosen time stays editable there).
  const handleSelectSlot = (startsAt: string) => {
    setSelectedSlot(startsAt);
    setError('');
    setHasNavigated(true);
    setStep('details');
  };

  const submitBooking = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canBook) return;
    setBooking(true);
    setError('');
    const response = await apiRequest<BookingResult>('POST', '/onboarding-call/book', {
      starts_at: selectedSlot,
      timezone,
      ...form,
      source
    });
    if (!response.success || !response.data) {
      setError(response.message || response.error || 'That time could not be booked.');
      setBooking(false);
      loadSlots();
      return;
    }
    setConfirmation(response.data);
    setBooking(false);
    onBooked?.();
  };

  if (loading) {
    return (
      <div className={`flex ${isModal ? 'min-h-[240px]' : 'min-h-[420px]'} items-center justify-center portal-typography`}>
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className={`text-center portal-typography ${isModal ? 'py-6' : 'mx-auto max-w-xl py-12'}`}>
        <ShieldCheck className="mx-auto mb-4 h-10 w-10 text-primary" aria-hidden="true" />
        <h1 className="text-2xl font-semibold text-foreground">Onboarding call unavailable</h1>
        <p className="mt-3 text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  // Page confirmation (route mode). The modal renders confirmation inside the main grid instead,
  // so switching to it doesn't resize the modal.
  if (confirmation && !isModal) {
    return (
      <div className="mx-auto max-w-3xl py-10 portal-typography">
        <button type="button" onClick={() => navigate('/portal/invest/properties')} className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to offerings
        </button>
        <section className="rounded-lg border border-border bg-card p-7 shadow-sm">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--success))]/10">
            <CheckCircle2 className="h-7 w-7 text-[hsl(var(--success))]" aria-hidden="true" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Onboarding call confirmed</p>
          <h1 className="mt-2 text-3xl font-semibold text-foreground">You are on the calendar.</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            {confirmation.formatted_time} is confirmed with {hostName}. A confirmation email with a calendar file has been sent to {email}.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Button onClick={() => navigate('/portal/invest/properties')}>Return to offerings</Button>
            <a href={confirmation.add_to_calendar_url} download="onboarding-call.ics" className="text-sm font-semibold text-foreground underline">Add to calendar</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={isModal ? 'grid portal-typography' : 'py-6 md:py-8 portal-typography'}>
      {/* Scheduling panel. Once booked (modal) it's removed from layout (`hidden`) so the
          confirmation sizes to its own compact content instead of the tall scheduler. The
          time↔details no-jump behavior is handled by the inner grid, not here. */}
      <div
        className={isModal ? `col-start-1 row-start-1 ${confirmation ? 'hidden' : ''}` : undefined}
        aria-hidden={isModal && !!confirmation ? true : undefined}
      >
      {!isModal && (
        <button type="button" onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      )}

      {/* Header */}
      <header className={isModal ? 'mb-6' : 'mb-8'}>
        <h1 className={`font-semibold leading-tight text-foreground ${isModal ? 'text-2xl' : 'text-3xl sm:text-4xl'}`}>{theme.title}</h1>
        <p className="mt-3 max-w-2xl leading-6 text-muted-foreground">{theme.body}</p>
        {config.upcoming_event && (
          <div className="mt-5 rounded-md border border-border bg-secondary px-4 py-3 text-sm">
            <p className="font-semibold text-foreground">You already have a call scheduled</p>
            <p className="mt-1 text-muted-foreground">{formatDay(config.upcoming_event.starts_at)} at {formatTime(config.upcoming_event.starts_at)}</p>
          </div>
        )}
      </header>

      {/* Scheduler */}
      <section className={isModal ? '' : 'rounded-lg border border-border bg-card p-6 shadow-sm'}>
        {error && (
          <div className="mb-5 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Both steps occupy the same grid cell; the inactive one is `invisible` but still sized,
            so the section height is always max(step 1, step 2) and never jumps when switching. */}
        <div className="grid">
          {/* Step 1 · Choose a time */}
          <div className={`col-start-1 row-start-1 pb-4 md:pb-0 ${step === 'time' ? (hasNavigated ? 'step-enter-back' : '') : 'hidden md:invisible'}`} aria-hidden={step !== 'time'}>
            <h2 className="text-xl font-semibold text-foreground">Choose a time</h2>

            {loadingSlots ? (
              <div className="mt-5 flex h-48 items-center justify-center rounded-md bg-secondary">
                <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
              </div>
            ) : calMonth && monthBounds ? (
              // Calendar + time list: side by side on desktop, stacked full-width on mobile.
              <>
                <div className="mt-5 grid gap-6 md:grid-cols-2">
                <MonthCalendar
                  month={calMonth}
                  onMonthChange={setCalMonth}
                  availableKeys={availableKeys}
                  selectedKey={selectedDateKey}
                  onSelect={(key) => setSelectedDateKey(key)}
                  minMonth={monthBounds.min}
                  maxMonth={monthBounds.max}
                />

                <div className="md:border-l md:border-border md:pl-6">
                  <p className="mb-3 text-sm font-medium text-foreground">
                    {selectedDaySlots[0] ? formatDay(selectedDaySlots[0].starts_at) : 'Select a date'}
                  </p>
                  <div className="flex flex-col gap-2">
                    {selectedDaySlots.map(slot => (
                      <button
                        type="button"
                        key={slot.starts_at}
                        onClick={() => handleSelectSlot(slot.starts_at)}
                        className={`h-11 w-full rounded-md border px-4 text-left text-sm font-medium transition-colors ${selectedSlot === slot.starts_at ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground md:hover:bg-muted'}`}
                      >
                        {formatTime(slot.starts_at)}
                      </button>
                    ))}
                    {!selectedDaySlots.length && (
                      <p className="rounded-md bg-secondary p-4 text-sm text-muted-foreground">No times available on this day.</p>
                    )}
                  </div>
                </div>
                </div>
                <p className="mt-4 text-xs text-muted-foreground">{config.meeting_type.duration_minutes}-minute call · Times shown in your browser timezone.</p>
              </>
            ) : (
              <p className="mt-5 rounded-md bg-secondary p-4 text-sm text-muted-foreground">No onboarding times are currently available.</p>
            )}
          </div>

          {/* Step 2 · Your details */}
          <form onSubmit={submitBooking} className={`col-start-1 row-start-1 ${step === 'details' ? 'step-enter-fwd' : 'hidden md:invisible'}`} aria-hidden={step !== 'details'}>
            {/* Chosen time — tap to change */}
            <button
              type="button"
              onClick={() => { setHasNavigated(true); setStep('time'); }}
              className="flex w-full items-center justify-between rounded-lg border border-border bg-secondary px-4 py-3 text-left transition-colors md:hover:bg-muted"
            >
              <span>
                <span className="block text-sm font-semibold text-foreground">{selectedSlot ? formatDay(selectedSlot) : ''}</span>
                <span className="block text-sm text-muted-foreground">{selectedSlot ? `${formatTime(selectedSlot)} · ${config.meeting_type.duration_minutes} min` : ''}</span>
              </span>
              <span className="text-sm font-semibold text-foreground underline">Change</span>
            </button>

            <h2 className="mt-6 text-xl font-semibold text-foreground">Your details</h2>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium text-foreground">
                First name
                <input required value={form.first_name} onChange={event => updateField('first_name', event.target.value)} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-foreground" />
              </label>
              <label className="text-sm font-medium text-foreground">
                Last name
                <input required value={form.last_name} onChange={event => updateField('last_name', event.target.value)} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-foreground" />
              </label>
              <label className="text-sm font-medium text-foreground sm:col-span-2">
                Phone
                <div className="relative mt-1">
                  <Phone className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(555) 123-4567"
                    value={form.phone}
                    onChange={event => updateField('phone', formatPhone(event.target.value))}
                    className="h-11 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm outline-none focus:border-foreground"
                  />
                </div>
              </label>
              <label className="text-sm font-medium text-foreground sm:col-span-2">
                Notes for the team
                <textarea value={form.notes} onChange={event => updateField('notes', event.target.value)} rows={3} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-foreground" />
              </label>
            </div>

            {/* Sticky footer CTA on mobile (always reachable); normal in-flow on desktop. */}
            <div className="sticky bottom-0 z-10 -mx-4 mt-6 border-t border-border bg-background px-4 pb-4 pt-3 md:static md:z-auto md:mx-0 md:border-0 md:bg-transparent md:p-0">
              <Button disabled={!canBook} type="submit" className="w-full gap-2">
                {booking && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {booking ? 'Booking…' : 'Confirm booking'}
              </Button>
            </div>
          </form>
        </div>
      </section>
      </div>

      {/* Confirmation panel (modal) — overlays the same grid cell, centered vertically. */}
      {isModal && confirmation && (
        <div className="col-start-1 row-start-1 flex flex-col items-center px-2 py-10 text-center">
          <h3 className="text-2xl font-semibold text-foreground">You're booked</h3>
          <p className="mt-3 max-w-lg text-base text-foreground">
            {confirmation.formatted_time} with {hostName}.
          </p>
          <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
            A confirmation and calendar invite were sent to {email}.
          </p>
          <div className="mt-8 flex w-full max-w-xs flex-col items-center gap-3">
            <Button onClick={onClose} className="w-full">Done</Button>
            <a href={confirmation.add_to_calendar_url} download="onboarding-call.ics" className="text-sm font-semibold text-foreground underline">Add to calendar</a>
          </div>
        </div>
      )}
    </div>
  );
}
